/**
 * Graph-color token accessor for the Reporting page.
 *
 * The design system exposes 10 semantic graph color tokens
 * (`--color-graph-primary` → `--color-graph-denary`) in
 * `packages/shared/src/styles/theme.css` and `packages/config/tailwind/theme.ts`.
 *
 * Each position is assigned a deterministic slot from this palette by
 * its index in the positions list, so the same position reads with the
 * same color everywhere it appears (Distributions bar, Balances line,
 * and any legend).
 *
 * Recharts accepts arbitrary CSS color strings as its `stroke` / `fill`
 * props, so the simplest, most correct wiring is to pass the raw
 * `rgb(var(--color-graph-n))` expression straight through — the
 * browser resolves it at paint time and a theme-switch would pick up
 * the new token value automatically.
 */

/** Ordered slots of the graph palette. */
const GRAPH_SLOTS = [
    "primary",
    "secondary",
    "tertiary",
    "quaternary",
    "quinary",
    "senary",
    "septenary",
    "octonary",
    "nonary",
    "denary"
] as const;

export type GraphSlot = (typeof GRAPH_SLOTS)[number];

/** CSS custom property name for a given palette slot. */
export function graphTokenVar(slot: GraphSlot): string {
    return `--color-graph-${slot}`;
}

/**
 * CSS color expression Recharts can consume as `stroke` / `fill`.
 * Keeps the value in CSS-var space so the palette stays theme-driven.
 */
export function graphColor(slot: GraphSlot): string {
    return `rgb(var(--color-graph-${slot}))`;
}

/** Same as `graphColor` but with an alpha multiplier (0–1). */
export function graphColorAlpha(slot: GraphSlot, alpha: number): string {
    return `rgb(var(--color-graph-${slot}) / ${alpha})`;
}

/**
 * Static slot → Tailwind background-class lookup. Use this when a swatch's
 * color is determined by a `GraphSlot` enum value rather than a runtime
 * color string — lets the consumer drop `style={{ backgroundColor }}` for
 * a plain `className` lookup. The class names match the `graph` palette
 * registered in `packages/config/tailwind/theme.ts`.
 */
export const GRAPH_BG_CLASS: Record<GraphSlot, string> = {
    primary: "bg-graph-primary",
    secondary: "bg-graph-secondary",
    tertiary: "bg-graph-tertiary",
    quaternary: "bg-graph-quaternary",
    quinary: "bg-graph-quinary",
    senary: "bg-graph-senary",
    septenary: "bg-graph-septenary",
    octonary: "bg-graph-octonary",
    nonary: "bg-graph-nonary",
    denary: "bg-graph-denary"
};

/**
 * Deterministic slot for a given positions-list index, wrapping at 10.
 *
 * Reversed order — first position gets the deepest slot (denary),
 * subsequent positions step toward lighter slots. The palette runs
 * light → mixed in token order, which meant the anchor series was
 * rendering on the palest hue; reversing puts visual weight on the
 * series the user is most likely to care about.
 */
export function graphSlotForIndex(index: number): GraphSlot {
    const n = GRAPH_SLOTS.length;
    return GRAPH_SLOTS[(n - 1 - (index % n)) % n];
}

/**
 * Build a stable `positionUuid → GraphSlot` mapping from an ordered
 * positions list. Pass the same list order everywhere that reads
 * colors so a position doesn't change color between reports.
 */
export function buildPositionColorMap<T extends { uuid: string }>(positions: readonly T[]): Record<string, GraphSlot> {
    const map: Record<string, GraphSlot> = {};
    positions.forEach((p, i) => {
        map[p.uuid] = graphSlotForIndex(i);
    });
    return map;
}
